import React, { useEffect, useMemo, useState } from "react";
import { SyncedStaffUser, FilersHubStaffRole, UserRole, WebhookConfig } from "../types";
import {
  updateFirmLogo,
  updateFirmSettings,
  ghlOAuthStart,
  subscribeToSyncedStaff,
  ghlSyncUsersNow,
  ghlGetStatus,
  firmAssignUserRole,
  inviteEmployee,
} from "../services/firestoreService";
import { useAuth } from "../context/AuthContext";

const FirmSettings: React.FC = () => {
  const { firmSettings, refreshFirmSettings, userDoc, firmId: contextFirmId, isFirmOwner } = useAuth();

  const firmId = useMemo(() => contextFirmId || (userDoc as any)?.firmId || null, [contextFirmId, userDoc]);

  // Managers only see Team, Owners see everything
  const [activeTab, setActiveTab] = useState<"branding" | "integrations" | "team">(isFirmOwner ? "branding" : "team");

  const [firmName, setFirmName] = useState(firmSettings.name || "");
  const [brandColor, setBrandColor] = useState(firmSettings.brandColor || "#0ea5e9");

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>(firmSettings.logoUrl || "");

  const [savingSettings, setSavingSettings] = useState(false);

  // Webhooks
  const [outboundUrl, setOutboundUrl] = useState(firmSettings.webhookConfig?.outboundUrl || "");
  const [webhookSecret, setWebhookSecret] = useState(firmSettings.webhookConfig?.secretKey || "");
  const [eventFlags, setEventFlags] = useState(
    firmSettings.webhookConfig?.events || {
      docUploaded: true,
      docSigned: true,
      statusChanged: true,
      messageReceived: true,
    }
  );

  // Team
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.TaxPro);
  const [inviteSuccess, setInviteSuccess] = useState("");

  const [ghlStatus, setGhlStatus] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncedStaff, setSyncedStaff] = useState<SyncedStaffUser[]>([]);

  useEffect(() => {
    setFirmName(firmSettings.name || "");
    setBrandColor(firmSettings.brandColor || "#0ea5e9");
    setLogoPreview(firmSettings.logoUrl || "");

    if (firmSettings.webhookConfig) {
      setOutboundUrl(firmSettings.webhookConfig.outboundUrl || "");
      setWebhookSecret(firmSettings.webhookConfig.secretKey || "");
      setEventFlags(
        firmSettings.webhookConfig.events || {
          docUploaded: true,
          docSigned: true,
          statusChanged: true,
          messageReceived: true,
        }
      );
    }
  }, [firmSettings]);

  useEffect(() => {
    if (!firmId) return;

    const fetchStatus = async () => {
      try {
        const status = await ghlGetStatus({ locationId: firmId });
        setGhlStatus((status as any).data || status);
      } catch (e) {
        console.error("GHL status fetch failed", e);
      }
    };

    fetchStatus();
  }, [firmId]);

  useEffect(() => {
    if (!firmId) return;
    if (activeTab !== "team") return;

    const unsub = subscribeToSyncedStaff(firmId, (users) => setSyncedStaff(users));
    return () => unsub();
  }, [firmId, activeTab]);

  const handleSaveAll = async () => {
    if (!firmId) return;

    setSavingSettings(true);
    try {
      const webhookConfig: WebhookConfig = {
        outboundUrl,
        secretKey: webhookSecret,
        events: eventFlags,
      };

      await updateFirmSettings(firmId, {
        name: firmName,
        brandColor,
        webhookConfig,
      });

      if (logoFile) await updateFirmLogo(firmId, logoFile, "main");

      await refreshFirmSettings();
      alert("Settings updated successfully.");
      setLogoFile(null);
    } catch (error) {
      console.error(error);
      alert("Failed to update settings.");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleConnectGHL = async () => {
    if (!firmId) {
      alert("Firm context is missing. Please refresh and try again.");
      return;
    }

    try {
      // IMPORTANT: must be tenant scoped or your app can not recover context after OAuth
      const desiredReturnUrl = `/location/${firmId}/settings`;

      const res: any = await ghlOAuthStart({ desiredReturnUrl });
      const authUrl = res?.data?.authUrl || res?.authUrl;

      if (authUrl) window.location.href = authUrl;
      else alert("OAuth Start failed: authUrl missing.");
    } catch (e) {
      console.error(e);
      alert("OAuth Start failed.");
    }
  };

  // ✅ This is the "Sync GHL Team" logic:
  // Calls the function that should pull location users into FilersHub (firms/{locationId}/users).
  const handleSyncNow = async () => {
    if (!firmId) return;

    setSyncing(true);
    try {
      await ghlSyncUsersNow({ locationId: firmId });

      // Refresh integration status (optional but keeps UI honest)
      const status = await ghlGetStatus({ locationId: firmId });
      setGhlStatus((status as any).data || status);

      alert("Team sync complete.");
      // No manual refresh needed for roster table because subscribeToSyncedStaff is live.
      // As soon as Firestore writes land, the roster updates.
    } catch (e) {
      console.error(e);
      alert("Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleRoleChange = async (ghlUserId: string, newRole: FilersHubStaffRole) => {
    if (!firmId) return;
    try {
      await firmAssignUserRole({ locationId: firmId, ghlUserId, filershubRole: newRole });
    } catch (e: any) {
      alert(e?.message || "Role update failed.");
    }
  };

  // Tabs list filtered by role
  const tabs = [
    { id: "branding", label: "🎨 Branding" },
    { id: "integrations", label: "🔗 Integrations" },
    { id: "team", label: "👥 Team" },
  ].filter((t) => isFirmOwner || t.id === "team");

  // If firmId is missing, show a real screen instead of “blank app”
  if (!firmId) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h2 className="text-xl font-black text-slate-900">Firm context missing</h2>
          <p className="text-slate-500 mt-2 text-sm">
            Your session is authenticated, but the tenant id was not resolved. This usually happens if you landed on a non-tenant route.
          </p>
          <button onClick={() => window.location.reload()} className="mt-6 px-6 py-3 rounded-xl bg-slate-900 text-white font-bold">
            Reload
          </button>
        </div>
      </div>
    );
  }

  const isConnected = !!ghlStatus?.connected;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Firm Management</h1>
          <p className="text-slate-500">Configure your branding, team, and integrations.</p>
        </div>

        {isFirmOwner && (
          <button
            onClick={handleSaveAll}
            disabled={savingSettings}
            className="bg-brand-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-brand-700 shadow-lg transition-all disabled:opacity-50 active:scale-95 flex items-center gap-2"
          >
            {savingSettings ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              "💾"
            )}
            Save Changes
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-8 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all ${
              activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-400 hover:text-slate-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "branding" && isFirmOwner && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Firm Display Name</label>
              <input
                type="text"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none font-bold text-slate-800 mb-6"
              />

              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Brand Color (Hex)</label>
              <div className="flex gap-2">
                <div className="w-12 h-12 rounded-xl border border-slate-200 flex-shrink-0" style={{ backgroundColor: brandColor }}></div>
                <input
                  type="text"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  placeholder="#0ea5e9"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-brand-500 outline-none font-mono font-bold text-slate-800"
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-2 italic">This color will be used for active navigation and highlights.</p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <span className="text-xl">🖼️</span> Firm Logo
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-8">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-3">Logo Upload</label>
                  <div className="aspect-video border-2 border-dashed border-slate-100 rounded-2xl flex items-center justify-center bg-slate-50 overflow-hidden relative group">
                    {logoPreview ? (
                      <img src={logoPreview} className="max-w-full max-h-full object-contain p-8" alt="Preview" />
                    ) : (
                      <div className="text-slate-400 text-sm font-bold">No logo set yet</div>
                    )}

                    <label
                      htmlFor="main-logo"
                      className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all cursor-pointer text-white font-bold text-xs"
                    >
                      Upload New Logo
                    </label>
                    <input
                      type="file"
                      id="main-logo"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          setLogoFile(e.target.files[0]);
                          setLogoPreview(URL.createObjectURL(e.target.files[0]));
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "integrations" && isFirmOwner && (
        <div className="space-y-8 animate-fade-in">
          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-brand-50 rounded-3xl flex items-center justify-center text-4xl shadow-sm">⚡</div>
              <div>
                <h3 className="text-2xl font-bold text-slate-800">Firm Integration</h3>
                <p className="text-slate-500">Sync your firm staff and provision portal owners.</p>
                {ghlStatus?.connected && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">Connected: {ghlStatus.locationId}</span>
                  </div>
                )}
              </div>
            </div>

            {ghlStatus?.connected ? (
              <div className="flex flex-col gap-3 w-full md:w-auto">
                <button
                  onClick={handleSyncNow}
                  disabled={syncing}
                  className="px-8 py-3 bg-brand-600 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-brand-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {syncing ? "Syncing..." : "🔄 Sync Staff Now"}
                </button>
                <button onClick={handleConnectGHL} className="text-xs font-bold text-slate-400 hover:text-slate-600 underline">
                  Reconnect Account
                </button>
              </div>
            ) : (
              <button
                onClick={handleConnectGHL}
                className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-black transition-all shadow-xl active:scale-95"
              >
                Connect Provider
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === "team" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden animate-fade-in">
          <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1 space-y-6">
              <div>
                <h3 className="font-bold text-lg text-slate-800 mb-2">Invite New Member</h3>
                <p className="text-sm text-slate-500">Direct invite staff members to manage returns.</p>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await inviteEmployee(firmId, inviteEmail, inviteName, inviteRole);
                  setInviteSuccess("Invite Sent!");
                  setInviteEmail("");
                  setInviteName("");
                }}
                className="space-y-4"
              >
                <input
                  required
                  placeholder="Full Name"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand-500"
                />
                <input
                  type="email"
                  required
                  placeholder="Email Address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand-500"
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as any)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand-500 bg-white"
                >
                  <option value={UserRole.TaxPro}>Tax Pro</option>
                  <option value={UserRole.Manager}>Manager</option>
                  <option value={UserRole.FirmOwner}>Firm Owner</option>
                </select>

                {inviteSuccess && <p className="text-xs text-emerald-600 font-bold">{inviteSuccess}</p>}

                <button className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold hover:bg-black transition-all">
                  Send Invite
                </button>
              </form>

              {/* ✅ New: Sync GHL Team button and connection hint */}
              <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black text-slate-700 uppercase tracking-widest">GHL Team Sync</p>
                    <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                      Pull your GoHighLevel location users into FilersHub Team. If users are missing, run a sync.
                    </p>
                    {!isConnected && (
                      <p className="text-[11px] text-amber-600 font-bold mt-2">
                        Not connected yet. Connect in the Integrations tab first.
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleSyncNow}
                    disabled={syncing || !isConnected}
                    className="px-4 py-2 rounded-xl bg-brand-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-brand-700 transition-all disabled:opacity-50 whitespace-nowrap"
                  >
                    {syncing ? "Syncing..." : "Sync GHL Team"}
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="flex items-center justify-between gap-3 mb-6">
                <h3 className="font-bold text-lg text-slate-800">Staff Roster</h3>

                {/* ✅ Duplicate quick action near table too (owners love buttons) */}
                <button
                  onClick={handleSyncNow}
                  disabled={syncing || !isConnected}
                  className="px-4 py-2 rounded-xl bg-slate-900 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50"
                  title={!isConnected ? "Connect provider first in Integrations tab" : "Sync location users from GHL"}
                >
                  {syncing ? "Syncing..." : "🔄 Sync GHL Team"}
                </button>
              </div>

              <div className="border border-slate-100 rounded-2xl overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100">
                    <tr>
                      <th className="px-6 py-4">User</th>
                      <th className="px-6 py-4">Role</th>
                      <th className="px-6 py-4">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-50">
                    {syncedStaff.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="px-6 py-8 text-sm text-slate-400 font-bold">
                          No staff synced yet.
                        </td>
                      </tr>
                    ) : (
                      syncedStaff.map((staff) => (
                        <tr key={staff.ghlUserId}>
                          <td className="px-6 py-4">
                            <div className="font-bold text-slate-800">{staff.name}</div>
                            <div className="text-xs text-slate-400">{staff.email}</div>
                          </td>

                          <td className="px-6 py-4">
                            <select
                              value={staff.filershubRole}
                              disabled={!isFirmOwner}
                              onChange={(e) => handleRoleChange(staff.ghlUserId, e.target.value as FilersHubStaffRole)}
                              className="bg-transparent border border-slate-200 rounded-lg px-2 py-1 text-xs font-bold text-slate-600 focus:ring-0 disabled:opacity-50"
                            >
                              <option value="Unassigned">Unassigned</option>
                              <option value="Tax Pro">Tax Pro</option>
                              <option value="Manager">Manager</option>
                              <option value="FirmOwner">Firm Owner</option>
                            </select>
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                staff.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"
                              }`}
                            >
                              {staff.active ? "Active" : "Offline"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {!isConnected && (
                <div className="mt-4 text-[11px] text-slate-500">
                  Tip: Go to <span className="font-bold">Integrations</span> and connect your provider so FilersHub can pull your location users.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirmSettings;
